import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { CATEGORY_COLORS, ISLAND_BY_SLUG } from "../lib/constants";
import { trackPageview } from "../lib/api";
import { useT } from "../lib/LangContext";

// -----------------------------------------------------------------------------
// HeroCarousel — multi-slide homepage hero.
//
// Contract: `articles` is an ordered array of article docs (0-4 items). One
// slide per article, one article per island in most cases. If the array is
// empty the component renders nothing. If the array has exactly 1 item the
// component renders that single slide with NO rotation UI — visually
// indistinguishable from the old HeroSection to preserve current mobile /
// small-screen density.
//
// SSR / prerender safety: the initial DOM always renders slide 0 in its
// active state (opacity 1). Rotation is enabled only in useEffect, so the
// server-rendered payload (used by the Emergent crawler-cache SSR layer)
// contains a real article's title/image/link. Bots and non-JS clients see a
// valid, complete hero without ever needing JS to run. No design regression.
//
// Rotation: crossfade only (opacity 0/1 transition), auto-advance every 7s,
// pause on pointer hover OR keyboard focus anywhere inside the hero. Manual
// nav via dots + prev/next arrows. Respects prefers-reduced-motion by
// disabling auto-advance (users can still click dots/arrows).
// -----------------------------------------------------------------------------

const ROTATE_INTERVAL_MS = 7000;

const SlideContent = ({ article, isActive, indexLabel, externalLinkOnly = false }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const { t, tCategory, timeAgo } = useT();
  const color = CATEGORY_COLORS[article.category] || "#0AADA0";
  const hasImage = !!article.image_url && !imgFailed;
  const islandMeta = ISLAND_BY_SLUG[article.island];

  return (
    <div
      className="absolute inset-0 transition-opacity duration-700 ease-in-out"
      style={{ opacity: isActive ? 1 : 0, pointerEvents: isActive ? "auto" : "none" }}
      aria-hidden={!isActive}
      data-testid={`hero-slide-${indexLabel}`}
      role="group"
      aria-roledescription="slide"
    >
      {hasImage ? (
        <img
          src={article.image_url}
          alt={article.title}
          onError={() => setImgFailed(true)}
          className="absolute inset-0 w-full h-full object-cover"
          loading={isActive ? "eager" : "lazy"}
        />
      ) : (
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ background: color }}
          data-testid={`hero-slide-fallback-${indexLabel}`}
        >
          <span className="font-serif-display italic text-white text-5xl">
            {tCategory(article.category)}
          </span>
        </div>
      )}
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.45)" }} />

      <div className="relative p-6 sm:p-10 lg:p-12 flex flex-col justify-end min-h-[420px] pb-14 sm:pb-16">
        <div className="mb-4 flex items-center gap-2 sm:gap-3 flex-wrap">
          {islandMeta && (
            <span
              className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] uppercase tracking-wider text-white"
              style={{ background: islandMeta.color }}
              data-testid={`hero-slide-${indexLabel}-island-badge`}
            >
              <span aria-hidden>{islandMeta.flag}</span>
              {islandMeta.name}
            </span>
          )}
          <span
            className="inline-block px-3 py-1 rounded-full text-[11px] uppercase tracking-wider text-white"
            style={{ background: color }}
            data-testid={`hero-slide-${indexLabel}-category-badge`}
          >
            {tCategory(article.category)}
          </span>
          {/*
            Honesty-gate 2026-02-13: the "FEATURED" pill only renders
            when the article is ACTUALLY manually pinned via the admin
            dashboard (backend emits `article.pinned === true`).
            Recency-pool slides — which are just "the most recent
            article for this island" and have no editorial curation
            behind them — now render only the relative timestamp, so
            we don't lie to readers about editorial intent. Prior
            behaviour rendered "FEATURED" on every slide unconditionally
            even when 0 articles were pinned.
          */}
          {article.pinned && (
            <span
              className="text-xs text-white/90 uppercase tracking-wider font-medium"
              data-testid={`hero-slide-${indexLabel}-pinned-badge`}
            >
              {t("featured")}
            </span>
          )}
          <span className="text-xs text-white/85 uppercase tracking-wider">
            {timeAgo(article.published_at)}
          </span>
        </div>

        <h1
          className="font-serif-display text-3xl sm:text-4xl lg:text-[44px] leading-tight text-white mb-3 max-w-3xl"
          data-testid={`hero-slide-${indexLabel}-title`}
        >
          {article.title}
        </h1>

        <p className="text-[15px] text-white/85 leading-relaxed max-w-2xl mb-6 line-clamp-3">
          {article.ai_summary}
        </p>

        <div className="flex items-center gap-4 flex-wrap">
          {externalLinkOnly ? (
            /* Papiamento hub uses this branch — we don't have a
               synthetic /article/:id detail page for raw PA source
               headlines (no AI rewriting), so the primary CTA links
               straight to the publisher. Preserves the "raw source,
               zero AI rewriting" editorial rule. */
            <a
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackPageview("/article/external", article.id)}
              tabIndex={isActive ? 0 : -1}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#E63946] hover:bg-[#cf2f3c] text-white text-sm transition-colors"
              data-testid={`hero-slide-${indexLabel}-read-btn`}
            >
              {t("read_story")} <ArrowRight size={16} />
            </a>
          ) : (
            <Link
              to={`/article/${article.id}${article.slug ? `-${article.slug}` : ""}`}
              onClick={() => trackPageview("/article", article.id)}
              tabIndex={isActive ? 0 : -1}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#E63946] hover:bg-[#cf2f3c] text-white text-sm transition-colors"
              data-testid={`hero-slide-${indexLabel}-read-btn`}
            >
              {t("read_story")} <ArrowRight size={16} />
            </Link>
          )}
          <a
            href={article.url}
            target="_blank"
            rel="noopener noreferrer"
            tabIndex={isActive ? 0 : -1}
            className="text-xs text-white/90 hover:text-white underline underline-offset-2"
            data-testid={`hero-slide-${indexLabel}-source-link`}
          >
            {t("read_full_article_cta")} →
          </a>
        </div>
        {!externalLinkOnly && (
          <p
            className="text-[10px] text-white/70 mt-2"
            data-testid={`hero-slide-${indexLabel}-gt-attr`}
          >
            {t("translated_via_google")} {article.source_name}
          </p>
        )}
      </div>
    </div>
  );
};

export const HeroCarousel = ({ articles, externalLinkOnly = false }) => {
  const { t } = useT();
  const list = Array.isArray(articles) ? articles.filter(Boolean) : [];
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const total = list.length;
  const multiSlide = total > 1;

  // Clamp the index if `articles` shrinks (e.g. lang switch returns fewer).
  useEffect(() => {
    if (index >= total && total > 0) setIndex(0);
  }, [total, index]);

  const goTo = useCallback((i) => {
    if (total === 0) return;
    setIndex(((i % total) + total) % total);
  }, [total]);
  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  // Auto-advance — only when JS-hydrated, multi-slide, not paused, and the
  // user hasn't opted out via prefers-reduced-motion.
  useEffect(() => {
    if (!multiSlide || paused) return;
    const reduce = typeof window !== "undefined"
      && window.matchMedia
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    timerRef.current = setTimeout(next, ROTATE_INTERVAL_MS);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [index, paused, multiSlide, next]);

  if (total === 0) return null;

  return (
    <section
      className="relative w-full overflow-hidden rounded-xl mb-8 fade-up"
      style={{ minHeight: 420 }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(e) => {
        // Only unpause when focus leaves the whole hero (not on internal moves)
        if (!e.currentTarget.contains(e.relatedTarget)) setPaused(false);
      }}
      aria-roledescription="carousel"
      aria-label={t("hero_choose_slide")}
      data-testid="featured-hero-carousel"
      data-hero-count={total}
    >
      {list.map((a, i) => (
        <SlideContent
          key={a.id || i}
          article={a}
          isActive={i === index}
          indexLabel={i}
          externalLinkOnly={externalLinkOnly}
        />
      ))}

      {multiSlide && (
        <>
          <button
            type="button"
            onClick={prev}
            aria-label={t("hero_prev_slide")}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-black/35 hover:bg-black/55 text-white transition-colors"
            data-testid="hero-carousel-prev"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            type="button"
            onClick={next}
            aria-label={t("hero_next_slide")}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-black/35 hover:bg-black/55 text-white transition-colors"
            data-testid="hero-carousel-next"
          >
            <ChevronRight size={20} />
          </button>

          <div
            className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2"
            role="tablist"
            aria-label={t("hero_choose_slide")}
            data-testid="hero-carousel-dots"
          >
            {list.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => goTo(i)}
                role="tab"
                aria-selected={i === index}
                aria-label={t("hero_slide_label").replace("{n}", i + 1).replace("{total}", total)}
                className="rounded-full transition-all"
                style={{
                  width: i === index ? 22 : 8,
                  height: 8,
                  background: i === index ? "#FFFFFF" : "rgba(255,255,255,0.55)",
                }}
                data-testid={`hero-carousel-dot-${i}`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
};
